/**
 * Template schema. A template is a tree of sections containing items.
 *
 * Two kinds of question item (ADR-0008):
 *  - fields:   capture information only (header data, site profile).
 *  - controls: assessed Yes/No/N/A items. A confirmed answer that differs from
 *              the control's `compliantAnswer` becomes a finding (ADR-0018).
 * Layout items (headings, info and warning boxes) carry no answer.
 *
 * Text that users see is a LocalizedText map keyed by language code.
 */
import { z } from 'zod';

const LANGUAGE_CODE_MESSAGE = 'Language code like "en" or "pt-BR"';
export const LanguageCode = z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/, LANGUAGE_CODE_MESSAGE);
export type LanguageCode = z.infer<typeof LanguageCode>;

/** A bad key is reported as `invalid_key`; keep the language-code message rather than zod's generic one. */
export const LocalizedText = z.record(LanguageCode, z.string(), { error: (iss) => (iss.code === 'invalid_key' ? LANGUAGE_CODE_MESSAGE : undefined) });
export type LocalizedText = z.infer<typeof LocalizedText>;

export const SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export const Severity = z.enum(SEVERITIES);
export type Severity = z.infer<typeof Severity>;

export const CONTROL_VALUES = ['YES', 'NO', 'NA'] as const;
export const ControlValue = z.enum(CONTROL_VALUES);
export type ControlValue = z.infer<typeof ControlValue>;

/** Stable key for a field's data, used in rules, reporting and the API. */
export const FieldKey = z.string().regex(/^[a-z][a-zA-Z0-9_.]*$/, 'camelCase key, dots allowed for grouping');
/** Control codes: global "PER-001", or country-local "CO-PER-201". */
export const ControlCode = z.string().regex(/^([A-Z]{2}-)?[A-Z]{2,4}-\d{3}$/, 'Control code like PER-001 or CO-PER-201');
export const SectionCode = z.string().regex(/^[A-Z]{2,4}$/);

// ---------------------------------------------------------------------------
// Rules (show/hide)
// ---------------------------------------------------------------------------

/** Values a rule can read: a field, a control's answer, or survey context. */
export const RuleRef = z.union([
  z.object({ field: FieldKey }),
  z.object({ control: ControlCode }),
  z.object({ context: z.enum(['site.region', 'site.country', 'site.serviceLine', 'site.branch']) }),
]);
export type RuleRef = z.infer<typeof RuleRef>;

export const RuleOp = z.enum([
  'eq', // value equals
  'neq', // value differs (or is empty)
  'in', // value is one of `value[]`
  'notIn',
  'includes', // multi-choice value contains `value`
  'includesAny', // multi-choice value contains any of `value[]`
  'answered',
  'notAnswered',
]);
export type RuleOp = z.infer<typeof RuleOp>;

export type Rule =
  | { all: Rule[] }
  | { any: Rule[] }
  | { not: Rule }
  | { ref: RuleRef; op: RuleOp; value?: unknown };

export const Rule: z.ZodType<Rule> = z.lazy(() =>
  z.union([
    z.object({ all: z.array(Rule).min(1) }),
    z.object({ any: z.array(Rule).min(1) }),
    z.object({ not: Rule }),
    z.object({ ref: RuleRef, op: RuleOp, value: z.unknown().optional() }),
  ]),
);

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

export const ChoiceOption = z.object({
  code: z.string().regex(/^[A-Z0-9_]+$/, 'UPPER_SNAKE option code'),
  label: LocalizedText,
  /** Optional suggestion shown when this option is picked (the mockup's pop-up notes). */
  hint: LocalizedText.optional(),
});
export type ChoiceOption = z.infer<typeof ChoiceOption>;

const ItemCommon = {
  /** Unique within the template. Stable across versions. */
  id: z.string().min(1),
  visibleWhen: Rule.optional(),
  /** Explanation of what the question means (consideration #5). */
  help: LocalizedText.optional(),
};

export const FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'email',
  'phone',
  'single_choice',
  'multi_choice',
  'yes_no',
  'rating',
  'date',
  'time',
  'datetime',
  'photo',
  'file',
  'signature',
  'gps',
  'address',
  'customer',
  'site',
  'branch',
  'user',
  'repeating_group',
  'calculated',
  'hidden',
] as const;
export const FieldType = z.enum(FIELD_TYPES);
export type FieldType = z.infer<typeof FieldType>;

export interface FieldItem {
  kind: 'field';
  id: string;
  key: string;
  type: FieldType;
  label: LocalizedText;
  help?: LocalizedText;
  placeholder?: LocalizedText;
  visibleWhen?: Rule;
  required?: boolean;
  options?: ChoiceOption[];
  display?: 'dropdown' | 'radio' | 'buttons' | 'checkboxes' | 'chips';
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  maxPhotos?: number;
  /** For `user` pickers: only users holding one of these role ids. */
  roleFilter?: string[];
  /** For `calculated`: a simple expression over other numeric field keys. */
  formula?: string;
  /** For `repeating_group`: the fields of each row. */
  fields?: FieldItem[];
  minRows?: number;
  maxRows?: number;
  /** Site-profile fields are reported as descriptive site data (ADR-0018). */
  siteProfile?: boolean;
}

export const FieldItem = z
  .object({
    kind: z.literal('field'),
    ...ItemCommon,
    key: FieldKey,
    type: FieldType,
    label: LocalizedText,
    placeholder: LocalizedText.optional(),
    required: z.boolean().optional(),
    options: z.array(ChoiceOption).optional(),
    display: z.enum(['dropdown', 'radio', 'buttons', 'checkboxes', 'chips']).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().optional(),
    unit: z.string().optional(),
    maxPhotos: z.number().int().positive().optional(),
    roleFilter: z.array(z.string()).optional(),
    formula: z.string().optional(),
    /** Recursion: a repeating group's row fields are field items too. */
    fields: z.array(z.lazy((): z.ZodType<FieldItem> => FieldItem)).optional(),
    minRows: z.number().int().nonnegative().optional(),
    maxRows: z.number().int().positive().optional(),
    siteProfile: z.boolean().optional(),
  })
  .superRefine((f, ctx) => {
    const needsOptions = f.type === 'single_choice' || f.type === 'multi_choice';
    if (needsOptions && (!f.options || f.options.length === 0)) {
      ctx.addIssue({ code: 'custom', message: `Field "${f.key}" of type ${f.type} needs options` });
    }
    if (f.type === 'repeating_group' && (!f.fields || f.fields.length === 0)) {
      ctx.addIssue({ code: 'custom', message: `Repeating group "${f.key}" needs fields` });
    }
    if (f.fields) {
      const subKeys = f.fields.map((x) => x.key);
      if (new Set(subKeys).size !== subKeys.length) ctx.addIssue({ code: 'custom', message: `Repeating group "${f.key}" has duplicate field keys` });
    }
    if (f.minRows !== undefined && f.maxRows !== undefined && f.minRows > f.maxRows) {
      ctx.addIssue({ code: 'custom', message: `Repeating group "${f.key}" has more minimum rows than maximum rows` });
    }
    if (f.type === 'calculated' && !f.formula) {
      ctx.addIssue({ code: 'custom', message: `Calculated field "${f.key}" needs a formula` });
    }
    if (f.options) {
      const codes = f.options.map((o) => o.code);
      if (new Set(codes).size !== codes.length) {
        ctx.addIssue({ code: 'custom', message: `Field "${f.key}" has duplicate option codes` });
      }
    }
  });

export const ControlItem = z.object({
  kind: z.literal('control'),
  ...ItemCommon,
  code: ControlCode,
  question: LocalizedText,
  /** The answer that means the control is in place. */
  compliantAnswer: z.enum(['YES', 'NO']),
  severity: Severity,
  /** Core controls cannot be removed or weakened in country copies (ADR-0010). */
  core: z.boolean().default(false),
  allowNA: z.boolean().default(true),
  /** When the reporter's comment is mandatory. */
  requireCommentOn: z.array(z.enum(['NON_COMPLIANT', 'NA'])).default([]),
  /** When at least one photo is mandatory. */
  requirePhotoOn: z.array(z.enum(['NON_COMPLIANT'])).default([]),
  /** Code of the global control this one was copied from (lineage). */
  originCode: ControlCode.optional(),
  /** True when a non-compliant answer represents an immediate life-safety hazard ("Stop-The-Line" H2). */
  lifeSafetyCritical: z.boolean().optional(),
  /** ISO / Regulatory compliance clauses satisfied by this control (e.g. ISO-45001:8.1.2, ISO-27001:A.11.1.1). */
  complianceClauses: z.array(z.string().regex(/^(ISO-45001|ISO-18788|ISO-27001|CTPAT|TAPA|OSHA):\S.{0,59}$/)).max(20).optional(),
  /** True when this control directly protects lone-worker or guard welfare (H5). */
  guardWelfare: z.boolean().optional(),
  /** Proof of presence (ADR-0034): whether a site's QR/NFC tag for this question must, may or need not be scanned. */
  checkpoint: z.enum(['NONE', 'OPTIONAL', 'REQUIRED']).default('NONE'),
});
export type ControlItem = z.infer<typeof ControlItem>;

export const LayoutItem = z.object({
  kind: z.literal('layout'),
  ...ItemCommon,
  type: z.enum(['heading', 'info', 'warning']),
  text: LocalizedText,
});
export type LayoutItem = z.infer<typeof LayoutItem>;

export type Item = FieldItem | ControlItem | LayoutItem;
export const Item: z.ZodType<Item> = z.discriminatedUnion('kind', [FieldItem, ControlItem, LayoutItem]);

export const Section = z.object({
  id: z.string().min(1),
  code: SectionCode,
  title: LocalizedText,
  description: LocalizedText.optional(),
  visibleWhen: Rule.optional(),
  items: z.array(Item),
});
export type Section = z.infer<typeof Section>;

export const Template = z
  .object({
    /** Lineage id — same across versions of one template. */
    id: z.string().min(1),
    surveyType: z.string().regex(/^[A-Z]{2,6}$/),
    name: LocalizedText,
    description: LocalizedText.optional(),
    sourceLanguage: LanguageCode,
    requiredLanguages: z.array(LanguageCode).min(1),
    currency: z.string().length(3).optional(),
    /** Id of the template this was copied from, if any. */
    copiedFrom: z.string().optional(),
    sections: z.array(Section).min(1),
  })
  .superRefine((t, ctx) => {
    if (!t.requiredLanguages.includes(t.sourceLanguage)) {
      ctx.addIssue({ code: 'custom', message: 'requiredLanguages must include the sourceLanguage' });
    }
    const ids = new Set<string>();
    const keys = new Set<string>();
    const codes = new Set<string>();
    const sectionCodes = new Set<string>();
    for (const s of t.sections) {
      if (sectionCodes.has(s.code)) ctx.addIssue({ code: 'custom', message: `Duplicate section code ${s.code}` });
      sectionCodes.add(s.code);
      for (const it of s.items) {
        if (ids.has(it.id)) ctx.addIssue({ code: 'custom', message: `Duplicate item id ${it.id}` });
        ids.add(it.id);
        if (it.kind === 'field') {
          if (keys.has(it.key)) ctx.addIssue({ code: 'custom', message: `Duplicate field key ${it.key}` });
          keys.add(it.key);
        }
        if (it.kind === 'control') {
          if (codes.has(it.code)) ctx.addIssue({ code: 'custom', message: `Duplicate control code ${it.code}` });
          codes.add(it.code);
        }
      }
    }
  });
export type Template = z.infer<typeof Template>;

// ---------------------------------------------------------------------------
// Answers & review
// ---------------------------------------------------------------------------

export const ControlPin = z.object({
  x: z.number().min(0).max(100),
  y: z.number().min(0).max(100),
  zone: z.string().max(100).optional(),
});
export type ControlPin = z.infer<typeof ControlPin>;

export const ControlAnswer = z.object({
  value: ControlValue.nullable(),
  /** Reporter's internal note — never shown to customers (ADR-0022). */
  note: z.string().optional(),
  /** Attachment ids of evidence photos/files. */
  photos: z.array(z.string()).default([]),
  /** Optional site map / floorplan hazard pin coordinates (0-100%) (F3). */
  pin: ControlPin.optional(),
  /** True if verified against previous baseline survey in Delta Survey mode (F2). */
  baselineVerified: z.boolean().optional(),
  /** ISO timestamp when escalated as a Stop-The-Line Priority-1 Life-Safety Alert (H2). */
  stopTheLineEscalatedAt: z.string().optional(),
  /** Deprecated: stamped by the device without a scan. Never counts as proof of presence (ADR-0034). */
  checkpointVerifiedAt: z.string().optional(),
  /** Deprecated (see checkpointVerifiedAt). */
  checkpointCode: z.string().max(80).optional(),
  /** The device's record of a tag scan for this control; the server's verification is authoritative (ADR-0034). */
  checkpointScan: z
    .object({
      scanId: z.string(),
      method: z.enum(['QR', 'NFC']),
      scannedAt: z.string(),
      status: z.enum(['PENDING', 'VERIFIED', 'REJECTED']),
      error: z.string().optional(),
      flags: z.array(z.string()).optional(),
    })
    .optional(),
});
export type ControlAnswer = z.infer<typeof ControlAnswer>;

export const Answers = z.object({
  fields: z.record(z.string(), z.unknown()).default({}),
  controls: z.record(z.string(), ControlAnswer).default({}),
});
export type Answers = z.infer<typeof Answers>;

export const ControlReview = z
  .object({
    decision: z.enum(['AGREE', 'CORRECT']),
    correctedValue: ControlValue.optional(),
    reason: z.string().optional(),
    /** Customer-facing text written by the analyst (ADR-0022). */
    customerObservation: z.string().optional(),
    recommendation: z.string().optional(),
    /** Photos the analyst chose to include in the customer report. */
    customerPhotos: z.array(z.string()).default([]),
    /** The analyst's severity for the finding, overriding the template's (with a reason). */
    severity: Severity.optional(),
    severityReason: z.string().max(2000).optional(),
    /** The analyst's due date for the finding (ISO date), overriding the one from severity (with a reason). */
    dueDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    dueDateReason: z.string().max(2000).optional(),
    reviewedBy: z.string(),
    reviewedAt: z.string(),
  })
  .superRefine((r, ctx) => {
    if (r.decision === 'CORRECT') {
      if (!r.correctedValue) ctx.addIssue({ code: 'custom', message: 'A correction needs a corrected value' });
      if (!r.reason || !r.reason.trim()) ctx.addIssue({ code: 'custom', message: 'A correction needs a reason' });
    }
    if (r.severity && !r.severityReason?.trim()) ctx.addIssue({ code: 'custom', path: ['severityReason'], message: 'A severity change needs a reason' });
    if (r.dueDate && !r.dueDateReason?.trim()) ctx.addIssue({ code: 'custom', path: ['dueDateReason'], message: 'A due date change needs a reason' });
  });
export type ControlReview = z.infer<typeof ControlReview>;

/** Context available to rules and reporting about where the survey happens. */
export interface SurveyContext {
  site: {
    region?: string | null;
    country?: string | null;
    serviceLine?: string | null;
    branch?: string | null;
  };
}
